import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot, query, doc, getDoc } from "firebase/firestore";
import { db } from "./firebaseConfig";

export interface MemberType {
    uid: string;
    name: string;
    phone: string;
    deleted: boolean;
    bankAccount: any[];
    savedKiosk: any[];
    timestamp: number;
    partnerId: string;
}

export interface PointType {
    id: string;
    docId?: string;
    inputAmt: string;
    inputType: string;
    kiosk: string;
    owner: string;
    pointAmt: number;
    pointDelta: number;
    partnerId: string;
    timestamp: number;
    type: "WITHDRAW" | "DEPOSIT" | "DONATE";
    completed?: boolean;
    member?: MemberType | null;
}

interface PointContextType {
    points: PointType[];
    withdraws: PointType[];
}

const PointContext = createContext<PointContextType | null>(null);

export const PointProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [points, setPoints] = useState<PointType[]>([]);
    const [withdraws, setWithdraws] = useState<PointType[]>([]);

    useEffect(() => {
        let active = true;
        const unsubscribe = onSnapshot(query(collection(db, "receipt")), async (snapshot) => {
            const dataList = snapshot.docs.map((doc) => ({
                ...(doc.data() as PointType),
                id: doc.id,
            }));

            const ownerIds = Array.from(new Set(dataList.map((item) => item.owner))).filter(Boolean);

            const memberPromises = ownerIds.map((ownerId) => getDoc(doc(db, "member", ownerId)));
            const memberDocs = await Promise.all(memberPromises);
            if (!active) return;

            const membersMap: Record<string, MemberType> = {};
            memberDocs.forEach((memberDoc) => {
                if (memberDoc.exists()) {
                    membersMap[memberDoc.id] = {
                        ...(memberDoc.data() as MemberType),
                        uid: memberDoc.id,
                    };
                }
            });

            const pointsWithMember = dataList.map((point) => ({
                ...point,
                member: membersMap[point.owner] || null,
            }));

            pointsWithMember.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

            setPoints(pointsWithMember);
            setWithdraws(pointsWithMember.filter((p) => p.type === "WITHDRAW" && p.completed !== true));
        });

        return () => { active = false; unsubscribe(); };
    }, []);

    return <PointContext.Provider value={{ points, withdraws }}>{children}</PointContext.Provider>;
};

export const usePoint = (): PointContextType => {
    const context = useContext(PointContext);
    if (!context) {
        throw new Error("usePoint must be used within a PointProvider");
    }
    return context;
};
